import React, { ReactNode, useMemo } from 'react';
import { Box, Divider, Link, SxProps, Theme, Typography } from '@mui/material';
import Markdown from 'react-markdown';
import rehypeSanitize, { defaultSchema } from 'rehype-sanitize';
import remarkDirective from 'remark-directive';
import remarkGfm from 'remark-gfm';
import { visit } from 'unist-util-visit';
import { AvButton } from './AvButton';

const headingStyle = { fontWeight: 600, mb: 0, mt: 3 };

type NestedListNode = {
  text: string;
  children: NestedListNode[];
};

const parseNestedListLines = (content: string): { indent: number; text: string }[] =>
  content
    .replace(/\u00A0/g, ' ')
    .split('\n')
    .map(line => line.replace(/[ \t]+$/, ''))
    .filter(line => line.trim().length > 0)
    .map(line => {
      const match = line.match(/^([ \t]*)([-*+•–—]|\d+\.)\s+(.+)$/);

      if (!match) {
        return null;
      }

      return {
        indent: match[1].length,
        text: match[3].trim(),
      };
    })
    .filter((item): item is { indent: number; text: string } => Boolean(item));

const buildNestedListTree = (items: { indent: number; text: string }[]): NestedListNode[] => {
  const root: NestedListNode[] = [];
  const stack: { indent: number; children: NestedListNode[] }[] = [{ indent: -1, children: root }];

  items.forEach(item => {
    while (stack.length > 1 && item.indent <= stack[stack.length - 1].indent) {
      stack.pop();
    }

    const node: NestedListNode = { text: item.text, children: [] };
    stack[stack.length - 1].children.push(node);
    stack.push({ indent: item.indent, children: node.children });
  });

  return root;
};

const toMarkdownListLines = (items: NestedListNode[], depth = 0): string[] =>
  items.flatMap(item => [`${'  '.repeat(depth)}- ${item.text}`, ...toMarkdownListLines(item.children, depth + 1)]);

const adaptNestedListContent = (content: string): string => {
  const normalizedText = content.replace(/\u00A0/g, ' ');
  const nonEmptyLines = normalizedText
    .split('\n')
    .map(line => line.replace(/[ \t]+$/, ''))
    .filter(line => line.trim().length > 0);
  const parsedLines = parseNestedListLines(normalizedText);
  const isNestedList = nonEmptyLines.length > 0 && parsedLines.length === nonEmptyLines.length;

  if (!isNestedList) {
    return normalizedText;
  }

  return toMarkdownListLines(buildNestedListTree(parsedLines)).join('\n');
};

const buildMarkdownComponents = (nestedList: boolean) => ({
  h1: ({ node, ...props }: { node?: any }) => <Box sx={headingStyle} {...props} />,
  h2: ({ node, ...props }: { node?: any }) => <Box sx={headingStyle} {...props} />,
  h3: ({ node, ...props }: { node?: any }) => <Box sx={headingStyle} {...props} />,
  h4: ({ node, ...props }: { node?: any }) => <Box sx={headingStyle} {...props} />,
  p: ({ node, ...props }: { node?: any }) => <Typography variant="body1" sx={{ mt: 1 }} {...props} />,
  a: ({ node, ...props }: { node?: any }) => (
    <AvButton variant="primaryText" component={Link} sx={{ display: 'contents' }} {...props} target="_blank" />
  ),
  hr: ({ node, ...props }: { node?: any }) => <Divider sx={{ my: 2, borderColor: 'var(--color-border-base-secondary)' }} {...props} />,
  ul: ({ node, ...props }: { node?: any }) => (
    <Box
      component="ul"
      sx={
        nestedList
          ? {
              pl: 3,
              m: 0,
              listStyleType: 'disc',
              listStylePosition: 'outside',
              '& ul': {
                pl: 3,
                listStyleType: 'circle',
              },
            }
          : {
              pl: 3,
              m: 0,
              listStyleType: 'disc',
              listStylePosition: 'outside',
              '& ul': { pl: 3, listStyleType: 'circle' },
            }
      }
      {...props}
    />
  ),
  ol: ({ node, ...props }: { node?: any }) => (
    <Box
      component="ol"
      sx={{
        pl: 3,
        mt: 0,
        mb: 1,
        listStyleType: 'decimal',
        listStylePosition: 'outside',
        '& ol': { pl: 3, listStyleType: 'lower-alpha' },
      }}
      {...props}
    />
  ),
  li: ({ node, ...props }: { node?: any }) => <Box component="li" sx={{ m: 0, '& p': { mt: 0 } }} {...props} />,
  blockquote: ({ node, ...props }: { node?: any }) => (
    <Box
      component="blockquote"
      sx={{
        borderLeft: `4px solid var(--color-surface-interactive-secondary-default)`,
        pl: 2,
        ml: 0,
        my: 2,
        fontStyle: 'italic',
      }}
      {...props}
    />
  ),
  pre: ({ node, children, ...props }: { node?: any; children?: any }) => (
    <Box
      component="pre"
      aria-label="code-block"
      sx={theme => ({
        background: 'var(--color-surface-base-tertiary)',
        borderRadius: theme.shape.borderRadius,
        fontFamily: 'monospace',
        overflowX: 'auto',
        whiteSpace: 'pre-wrap',
        wordBreak: 'break-word',
        px: 1,
        py: 0.5,
      })}
      {...props}>
      {children}
    </Box>
  ),
  code: ({ node, children, ...props }: { node?: any; children?: any }) => (
    <Box
      component="code"
      sx={theme => ({
        background: 'var(--color-surface-base-tertiary)',
        borderRadius: theme.shape.borderRadius,
        fontFamily: 'monospace',
      })}
      {...props}>
      {children}
    </Box>
  ),
  table: ({ node, ...props }: { node?: any }) => (
    <Box
      component="table"
      sx={{
        width: '100%',
        my: 2,
        borderCollapse: 'collapse',
      }}
      {...props}
    />
  ),
  thead: ({ node, ...props }: { node?: any }) => (
    <Box component="thead" sx={{ background: 'var(--color-surface-base-secondary)' }} {...props} />
  ),
  tr: ({ node, ...props }: { node?: any }) => (
    <Box component="tr" sx={{ borderBottom: '1px solid var(--color-border-base-primary)' }} {...props} />
  ),
  th: ({ node, ...props }: { node?: any }) => <Box component="th" sx={{ textAlign: 'left', p: 1.5, fontWeight: 600 }} {...props} />,
  td: ({ node, ...props }: { node?: any }) => <Box component="td" sx={{ p: 1.5, verticalAlign: 'top' }} {...props} />,
});

const defaultMarkdownComponents = buildMarkdownComponents(false);
const nestedListMarkdownComponents = buildMarkdownComponents(true);

const DIRECTIVE_NODE_TYPES = new Set(['textDirective', 'leafDirective', 'containerDirective']);

const remarkDirectiveToHast = () => (tree: any) => {
  visit(tree, (node: any) => {
    if (DIRECTIVE_NODE_TYPES.has(node.type)) {
      node.data = node.data || {};
      node.data.hName = node.name;
      node.data.hProperties = node.attributes || {};
    }
  });
};

type DirectiveDefinition = {
  directive: string;
  renderer: (props: Record<string, string>) => ReactNode;
  allowedAttributes?: string[];
};

const DEFAULT_DIRECTIVE_ATTRIBUTES = ['text', 'href', 'src', 'label', 'value', 'id', 'type', 'name'];

const buildSanitizeSchema = (directives: DirectiveDefinition[]) => {
  if (!directives.length) return defaultSchema;
  return {
    ...defaultSchema,
    tagNames: [...(defaultSchema.tagNames || []), ...directives.map(d => d.directive)],
    attributes: {
      ...defaultSchema.attributes,
      ...Object.fromEntries(directives.map(d => [d.directive, d.allowedAttributes ?? DEFAULT_DIRECTIVE_ATTRIBUTES])),
    },
  };
};

const hoistTablesFromListItems = (content: string): string => content.replace(/(^|\n)- (\*\*[^*\n]+\*\*)\n(\|)/g, '$1$2\n\n$3');

const unescapeLiteralNewlines = (content: string): string => content.replace(/\\n/g, '\n');

const AvMarkdown = ({
  content,
  sx = {},
  nestedList = false,
  directives = [],
}: {
  content: string;
  sx?: SxProps<Theme>;
  nestedList?: boolean;
  directives?: DirectiveDefinition[];
}) => {
  const processedContent = hoistTablesFromListItems(unescapeLiteralNewlines(nestedList ? adaptNestedListContent(content) : content));

  const isSafeProp = ([key, value]: [string, unknown]): boolean =>
    !key.toLowerCase().startsWith('on') && !(typeof value === 'string' && value.trim().toLowerCase().startsWith('javascript:'));

  const components = useMemo(() => {
    const base = nestedList ? nestedListMarkdownComponents : defaultMarkdownComponents;
    if (!directives.length) return base;
    const directiveComponents = Object.fromEntries(
      directives.map(({ directive, renderer }) => [
        directive,
        ({ node, ...props }: { node?: any; [key: string]: any }) =>
          renderer(Object.fromEntries(Object.entries(props).filter(isSafeProp)) as Record<string, string>),
      ])
    );
    return { ...base, ...directiveComponents };
  }, [nestedList, directives]);

  return (
    <Box sx={{ wordBreak: 'break-word', ...sx }}>
      <Markdown
        remarkPlugins={[remarkGfm, remarkDirective, remarkDirectiveToHast]}
        rehypePlugins={[[rehypeSanitize, buildSanitizeSchema(directives)]]}
        components={components}>
        {processedContent}
      </Markdown>
    </Box>
  );
};

export type { DirectiveDefinition };
export { AvMarkdown };
